<template>
  <router-view />
  <div
    ref="auditFabWrapRef"
    class="audit-fab-wrap"
    :style="{ top: auditFabCenterY + 'px', transform: 'translateY(-50%)' }"
  >
    <van-button
      type="primary"
      round
      size="small"
      class="audit-fab"
    >
      审核结果
    </van-button>
  </div>
  <van-popup
    v-model:show="auditPanelShow"
    position="bottom"
    round
    :style="{ height: '72%' }"
    @closed="onAuditPanelClosed"
  >
    <div class="audit-panel">
      <div class="audit-panel-title">我的登记与审核</div>
      <p class="audit-panel-hint">打开期间每 5 秒自动刷新</p>
      <van-loading v-if="auditLoading && !auditRecords.length" vertical class="audit-first-load">
        加载中...
      </van-loading>
      <van-empty
        v-if="!auditLoading && !auditRecords.length"
        description="暂无登记记录"
      />
      <div v-if="latestAuditRow" class="audit-list">
        <div
          class="audit-item audit-item-latest"
        >
          <div class="audit-latest-inner">
            <span class="audit-latest-tag" aria-label="最新一条">最新</span>
            <div class="audit-latest-main">
              <div class="audit-item-head">
                <span class="audit-name">{{ latestAuditRow.visitorName || '访客' }}</span>
                <span :class="['audit-status', 'st-' + latestAuditRow.auditStatus]">{{ auditStatusText(latestAuditRow.auditStatus) }}</span>
              </div>
              <div class="audit-meta">
                提交 {{ formatTime(latestAuditRow.createTime) }}
                <template v-if="latestAuditRow.visitDate">
                  · 到访 {{ latestAuditRow.visitDate }}
                  <template v-if="latestAuditRow.visitTime"> {{ latestAuditRow.visitTime }}</template>
                </template>
              </div>
              <div v-if="latestAuditRow.auditRemark" class="audit-remark">
                备注：{{ latestAuditRow.auditRemark }}
              </div>
              <div
                v-if="latestAuditRow.auditStatus === 1 && latestAuditRow.auditPassQr"
                class="audit-qr-wrap"
              >
                <div class="audit-qr-label">入厂凭证</div>
                <img
                  v-if="isQrImageUrl(latestAuditRow.auditPassQr)"
                  :src="latestAuditRow.auditPassQr"
                  alt="入厂凭证"
                  class="qr-img"
                >
                <img
                  v-else-if="latestQrDataUrl"
                  :src="latestQrDataUrl"
                  alt="入厂凭证"
                  class="qr-img"
                >
              </div>
            </div>
          </div>
        </div>

        <div v-if="olderAuditRows.length" class="audit-more-section">
          <van-button
            block
            plain
            size="small"
            type="primary"
            class="audit-more-toggle"
            @click="auditMoreExpanded = !auditMoreExpanded"
          >
            {{ auditMoreExpanded ? '收起更多记录' : `更多记录（${olderAuditRows.length}）` }}
          </van-button>
          <van-collapse
            v-show="auditMoreExpanded"
            v-model="olderAuditCollapseNames"
            class="audit-older-collapse"
          >
            <van-collapse-item
              v-for="row in olderAuditRows"
              :key="'fab-' + row.id"
              :name="String(row.id)"
              :title="auditOlderTitle(row)"
            >
              <div class="audit-item-head">
                <span class="audit-name">{{ row.visitorName || '访客' }}</span>
                <span :class="['audit-status', 'st-' + row.auditStatus]">{{ auditStatusText(row.auditStatus) }}</span>
              </div>
              <div class="audit-meta">
                提交 {{ formatTime(row.createTime) }}
                <template v-if="row.visitDate">
                  · 到访 {{ row.visitDate }}
                  <template v-if="row.visitTime"> {{ row.visitTime }}</template>
                </template>
              </div>
              <div v-if="row.auditRemark" class="audit-remark">
                备注：{{ row.auditRemark }}
              </div>
              <div v-if="row.auditStatus === 1 && row.auditPassQr" class="audit-qr-wrap">
                <div class="audit-qr-label">入厂凭证</div>
                <img
                  v-if="isQrImageUrl(row.auditPassQr)"
                  :src="row.auditPassQr"
                  alt="入厂凭证"
                  class="qr-img"
                >
                <template v-else>
                  <van-button
                    v-if="!auditMoreQrMap[row.id]"
                    type="primary"
                    plain
                    size="small"
                    :loading="auditMoreQrLoadingId === row.id"
                    @click="loadFabMoreQr(row)"
                  >
                    显示二维码
                  </van-button>
                  <img
                    v-else
                    :src="auditMoreQrMap[row.id]"
                    alt="入厂凭证"
                    class="qr-img"
                  >
                </template>
              </div>
            </van-collapse-item>
          </van-collapse>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script>
import { ref, onUnmounted, onMounted, nextTick, computed, reactive } from 'vue'
import { showToast } from 'vant'
import QRCode from 'qrcode'
import { getMyVisitRecords } from './api/api'
import { prefetchWorkflowHostCompaniesInBackground } from './utils/workflowHostCompaniesCache'
import router from './router'
import {
  goWechatAuthorize,
  isWeixinBrowser,
  VISITOR_OPEN_AUDIT_AFTER_OAUTH,
  VISITOR_OAUTH_RETURN_HASH,
  VISITOR_PENDING_OPEN_AUDIT_PANEL
} from './utils/wechatOAuth'

const AUDIT_FAB_Y_KEY = 'visitor_audit_fab_center_y'
const FAB_TAP_PX = 10
const FAB_FALLBACK_HALF_H = 52
/** 无本地记录时相对「视口垂直居中」再下移的像素，避免贴屏幕中线 */
const AUDIT_FAB_DEFAULT_OFFSET_DOWN_PX = 40

export default {
  name: 'App',
  setup () {
    const auditPanelShow = ref(false)
    const auditRecords = ref([])
    const auditLoading = ref(false)
    const latestQrDataUrl = ref('')
    const auditMoreExpanded = ref(false)
    const olderAuditCollapseNames = ref([])
    const auditMoreQrMap = reactive({})
    const auditMoreQrLoadingId = ref(null)

    const latestAuditRow = computed(() =>
      Array.isArray(auditRecords.value) && auditRecords.value.length ? auditRecords.value[0] : null
    )
    const olderAuditRows = computed(() =>
      Array.isArray(auditRecords.value) && auditRecords.value.length > 1
        ? auditRecords.value.slice(1)
        : []
    )
    const auditFabWrapRef = ref(null)
    const auditFabCenterY = ref(
      typeof window !== 'undefined'
        ? window.innerHeight / 2 + AUDIT_FAB_DEFAULT_OFFSET_DOWN_PX
        : 400 + AUDIT_FAB_DEFAULT_OFFSET_DOWN_PX
    )
    let pollTimer = null
    let fabResizeHandler = null
    let fabTouchCleanup = () => {}

    const getSafeInset = (prop) => {
      try {
        const v = getComputedStyle(document.documentElement).getPropertyValue(prop)
        const n = parseInt(v, 10)
        return Number.isNaN(n) ? 0 : n
      } catch (e) {
        return 0
      }
    }

    const getFabHalfHeight = () => {
      const el = auditFabWrapRef.value
      if (el) {
        const h = el.getBoundingClientRect().height
        if (h > 0) return h / 2
      }
      return FAB_FALLBACK_HALF_H
    }

    const clampFabY = (y) => {
      const winH = window.innerHeight
      const half = getFabHalfHeight()
      const margin = 16
      const yMin = margin + getSafeInset('env(safe-area-inset-top)') + half
      const yMax = winH - margin - getSafeInset('env(safe-area-inset-bottom)') - half
      if (yMax < yMin) return winH / 2
      return Math.min(yMax, Math.max(yMin, y))
    }

    const auditStatusText = (s) => {
      if (s === 1) return '通过'
      if (s === 2) return '拒绝'
      return '待审核'
    }

    const formatTime = (t) => {
      if (t == null) return ''
      return String(t).replace('T', ' ').slice(0, 19)
    }

    const isQrImageUrl = (s) => {
      if (s == null || s === '') return false
      const u = String(s).trim().toLowerCase()
      return u.startsWith('http://') || u.startsWith('https://')
    }

    const prefetchLatestQr = async (latest) => {
      latestQrDataUrl.value = ''
      if (!latest || latest.auditStatus !== 1 || !latest.auditPassQr || isQrImageUrl(latest.auditPassQr)) {
        return
      }
      try {
        latestQrDataUrl.value = await QRCode.toDataURL(String(latest.auditPassQr), { width: 200, margin: 1 })
      } catch (e) {
        latestQrDataUrl.value = ''
      }
    }

    const pruneFabMoreQr = () => {
      const ids = new Set((olderAuditRows.value || []).map(r => String(r.id)))
      Object.keys(auditMoreQrMap).forEach(k => {
        if (!ids.has(String(k))) {
          delete auditMoreQrMap[k]
        }
      })
    }

    const loadFabMoreQr = async (row) => {
      if (!row || !row.auditPassQr || isQrImageUrl(row.auditPassQr)) return
      if (auditMoreQrMap[row.id]) return
      auditMoreQrLoadingId.value = row.id
      try {
        auditMoreQrMap[row.id] = await QRCode.toDataURL(String(row.auditPassQr), { width: 200, margin: 1 })
      } catch (e) {
        auditMoreQrMap[row.id] = ''
      } finally {
        auditMoreQrLoadingId.value = null
      }
    }

    const auditOlderTitle = (row) => {
      const t = formatTime(row?.createTime)
      return `#${row?.id ?? ''}${t ? ' · ' + t : ''}`
    }

    const loadRecords = async () => {
      const openid = localStorage.getItem('openid')
      if (!openid) return
      auditLoading.value = true
      try {
        const list = await getMyVisitRecords(openid)
        const arr = Array.isArray(list) ? list : []
        auditRecords.value = arr
        await prefetchLatestQr(latestAuditRow.value)
        pruneFabMoreQr()
      } catch (e) {
        auditRecords.value = []
        latestQrDataUrl.value = ''
      } finally {
        auditLoading.value = false
      }
    }

    const clearPoll = () => {
      if (pollTimer != null) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }

    const openAuditPanel = () => {
      const openid = localStorage.getItem('openid')
      if (!openid) {
        if (!isWeixinBrowser()) {
          showToast({
            message: '请在微信中打开后再查看审核结果',
            position: 'middle'
          })
          return
        }
        try {
          sessionStorage.setItem(VISITOR_OPEN_AUDIT_AFTER_OAUTH, '1')
          sessionStorage.setItem(VISITOR_OAUTH_RETURN_HASH, window.location.hash || '#/')
        } catch (e) {}
        goWechatAuthorize()
        return
      }
      clearPoll()
      auditPanelShow.value = true
      auditLoading.value = true
      loadRecords()
      pollTimer = setInterval(loadRecords, 5000)
    }

    const tryConsumePendingAuditPanel = (to) => {
      if (!to || to.name === 'AuthCallback') {
        return
      }
      let pending = false
      try {
        pending = sessionStorage.getItem(VISITOR_PENDING_OPEN_AUDIT_PANEL) === '1'
      } catch (e) {
        pending = false
      }
      if (!pending) return
      if (!localStorage.getItem('openid')) return
      try {
        sessionStorage.removeItem(VISITOR_PENDING_OPEN_AUDIT_PANEL)
      } catch (e) {}
      clearPoll()
      auditPanelShow.value = true
      auditLoading.value = true
      loadRecords()
      pollTimer = setInterval(loadRecords, 5000)
    }

    const onAuditPanelClosed = () => {
      clearPoll()
    }

    onMounted(() => {
      prefetchWorkflowHostCompaniesInBackground()
      router.afterEach((to) => {
        nextTick(() => tryConsumePendingAuditPanel(to))
      })
      router.isReady().then(() => {
        nextTick(() => tryConsumePendingAuditPanel(router.currentRoute.value))
      })

      let savedY = null
      try {
        const raw = localStorage.getItem(AUDIT_FAB_Y_KEY)
        if (raw != null) {
          const n = parseFloat(raw)
          if (!Number.isNaN(n)) savedY = n
        }
      } catch (e) {}
      auditFabCenterY.value = clampFabY(
        savedY != null
          ? savedY
          : window.innerHeight / 2 + AUDIT_FAB_DEFAULT_OFFSET_DOWN_PX
      )
      nextTick(() => {
        auditFabCenterY.value = clampFabY(auditFabCenterY.value)
      })

      fabResizeHandler = () => {
        auditFabCenterY.value = clampFabY(auditFabCenterY.value)
      }
      window.addEventListener('resize', fabResizeHandler)

      const wrap = auditFabWrapRef.value
      if (wrap) {
        let dragStartY = 0
        let dragStartCenterY = 0
        let maxMoved = 0
        let dragActive = false

        const persistFabY = () => {
          try {
            localStorage.setItem(
              AUDIT_FAB_Y_KEY,
              String(Math.round(auditFabCenterY.value))
            )
          } catch (e) {}
        }

        const finishDragOrTap = () => {
          if (!dragActive) return
          dragActive = false
          if (maxMoved >= FAB_TAP_PX) {
            persistFabY()
          } else {
            openAuditPanel()
          }
          maxMoved = 0
        }

        const onTouchStart = (e) => {
          if (e.touches.length !== 1) return
          dragActive = true
          maxMoved = 0
          dragStartY = e.touches[0].clientY
          dragStartCenterY = auditFabCenterY.value
        }
        const onTouchMove = (e) => {
          if (!dragActive || e.touches.length !== 1) return
          e.preventDefault()
          const dy = e.touches[0].clientY - dragStartY
          maxMoved = Math.max(maxMoved, Math.abs(dy))
          auditFabCenterY.value = clampFabY(dragStartCenterY + dy)
        }
        const onTouchEnd = () => finishDragOrTap()
        const onTouchCancel = () => {
          if (!dragActive) return
          dragActive = false
          maxMoved = 0
        }

        const onMouseDown = (e) => {
          if (e.button !== 0) return
          dragActive = true
          maxMoved = 0
          dragStartY = e.clientY
          dragStartCenterY = auditFabCenterY.value
        }
        const onMouseMove = (e) => {
          if (!dragActive) return
          const dy = e.clientY - dragStartY
          maxMoved = Math.max(maxMoved, Math.abs(dy))
          auditFabCenterY.value = clampFabY(dragStartCenterY + dy)
        }
        const onMouseUp = () => finishDragOrTap()

        wrap.addEventListener('touchstart', onTouchStart, { passive: true })
        wrap.addEventListener('touchmove', onTouchMove, { passive: false })
        wrap.addEventListener('touchend', onTouchEnd)
        wrap.addEventListener('touchcancel', onTouchCancel)
        wrap.addEventListener('mousedown', onMouseDown)
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)

        fabTouchCleanup = () => {
          wrap.removeEventListener('touchstart', onTouchStart)
          wrap.removeEventListener('touchmove', onTouchMove)
          wrap.removeEventListener('touchend', onTouchEnd)
          wrap.removeEventListener('touchcancel', onTouchCancel)
          wrap.removeEventListener('mousedown', onMouseDown)
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
        }
      }
    })

    onUnmounted(() => {
      clearPoll()
      if (fabResizeHandler) {
        window.removeEventListener('resize', fabResizeHandler)
        fabResizeHandler = null
      }
      fabTouchCleanup()
    })

    return {
      auditPanelShow,
      auditRecords,
      auditLoading,
      latestQrDataUrl,
      latestAuditRow,
      olderAuditRows,
      auditMoreExpanded,
      olderAuditCollapseNames,
      auditMoreQrMap,
      auditMoreQrLoadingId,
      loadFabMoreQr,
      auditOlderTitle,
      auditFabWrapRef,
      auditFabCenterY,
      openAuditPanel,
      onAuditPanelClosed,
      auditStatusText,
      formatTime,
      isQrImageUrl
    }
  }
}
</script>

<style lang="scss">
#app {
  min-height: 100vh;
  background-color: #f7f8fa;
}

.audit-fab-wrap {
  position: fixed;
  right: 12px;
  z-index: 9999;
  touch-action: none;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.audit-fab {
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  writing-mode: vertical-rl;
  letter-spacing: 0.2em;
  padding: 14px 10px;
  line-height: 1.4;
  height: auto;
  min-width: 40px;
  pointer-events: none;
}

.audit-panel {
  padding: 16px;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
}

.audit-panel-title {
  font-size: 17px;
  font-weight: 600;
  text-align: center;
  color: #323233;
}

.audit-panel-hint {
  font-size: 12px;
  color: #969799;
  text-align: center;
  margin: 6px 0 12px;
}

.audit-first-load {
  padding: 32px 0;
  text-align: center;
}

.audit-list {
  padding-bottom: 24px;
}

.audit-item-latest {
  padding-top: 10px;
}

.audit-latest-inner {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
}

.audit-latest-tag {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 11px;
  color: #fff;
  background: #1989fa;
  padding: 3px 8px;
  border-radius: 4px;
  line-height: 1.3;
  white-space: nowrap;
}

.audit-latest-main {
  flex: 1;
  min-width: 0;
}

.audit-more-section {
  margin-top: 8px;
}

.audit-more-toggle {
  margin-bottom: 10px;
}

.audit-older-collapse :deep(.van-collapse-item__title) {
  font-size: 13px;
}

.audit-item {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.audit-item-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.audit-name {
  font-weight: 500;
  color: #323233;
}

.audit-status {
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 4px;

  &.st-0 {
    color: #ed6a0c;
    background: #fff7e6;
  }

  &.st-1 {
    color: #07c160;
    background: #e8f9ef;
  }

  &.st-2 {
    color: #ee0a24;
    background: #fee;
  }
}

.audit-meta {
  font-size: 12px;
  color: #969799;
  line-height: 1.5;
}

.audit-remark {
  font-size: 13px;
  color: #646566;
  margin-top: 8px;
}

.audit-qr-wrap {
  margin-top: 12px;
  text-align: center;
}

.audit-qr-label {
  font-size: 12px;
  color: #969799;
  margin-bottom: 8px;
}

.qr-img {
  max-width: 200px;
  width: 100%;
  height: auto;
  display: inline-block;
  vertical-align: top;
}

/* 适配移动端 */
@media screen and (max-width: 768px) {
  html {
    font-size: 16px;
  }
}

/* 通用样式 */
.page-container {
  min-height: 100vh;
  padding-bottom: 20px;
}

.card {
  margin: 12px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.card-title {
  padding: 16px;
  font-size: 16px;
  font-weight: 500;
  color: #323233;
  border-bottom: 1px solid #ebedf0;
}

.card-content {
  padding: 16px;
}

.submit-btn {
  margin: 20px 12px;
}

.auth-btn {
  margin: 20px 12px;
}

.success-icon {
  color: #07c160;
  font-size: 64px;
  margin-bottom: 16px;
}

.success-text {
  color: #323233;
  font-size: 18px;
  font-weight: 500;
}

.tips-text {
  color: #969799;
  font-size: 14px;
  margin-top: 8px;
}
</style>
